import apiKeysIcon from "@/assets/integrations/api-keys.svg";
import chromeIcon from "@/assets/integrations/chrome.svg";
import dynamics365Icon from "@/assets/integrations/dynamics365.svg";
import edgeIcon from "@/assets/integrations/edge.svg";
import gmailIcon from "@/assets/integrations/gmail.svg";
import googleCalendarIcon from "@/assets/integrations/google-calendar.svg";
import hubspotIcon from "@/assets/integrations/hubspot.svg";
import makeIcon from "@/assets/integrations/make.svg";
import n8nIcon from "@/assets/integrations/n8n.svg";
import odooIcon from "@/assets/integrations/odoo.svg";
import outlookCalendarIcon from "@/assets/integrations/outlook-calendar.svg";
import outlookIcon from "@/assets/integrations/outlook.svg";
import pipedriveIcon from "@/assets/integrations/pipedrive.svg";
import salesforceIcon from "@/assets/integrations/salesforce.svg";
import seliarisIcon from "@/assets/integrations/seliaris.png";
import zapierIcon from "@/assets/integrations/zapier.svg";
import { TFunction } from "i18next";

export type Integration = {
  key: string;
  title: string;
  description: string;
  available: boolean;
  beta: boolean;
  logo: string;
  link?: string;
};

export type IntegrationCategory = {
  category: string;
  title: string;
  items: Integration[];
};

const HUBSPOT_OAUTH_SCOPES = [
  "oauth",
  "crm.objects.contacts.write",
  "crm.objects.contacts.read",
  "crm.objects.companies.read",
  "crm.objects.companies.write",
  "crm.schemas.companies.write",
  "crm.schemas.companies.read",
  "crm.schemas.contacts.read",
  "crm.schemas.contacts.write",
  "crm.lists.read",
  "crm.lists.write",
] as const;

/**
 * Builds the HubSpot OAuth authorization URL with all required scopes.
 *
 * `state` is issued by the API and echoed back on the callback, so the code we
 * redeem is provably the one this team started. Without it, a link crafted
 * against someone else's portal would bind that portal to whoever opens it.
 *
 * Returns null if the client ID env var is missing.
 */
export const buildHubSpotOAuthUrl = (
  redirectUri: string,
  state: string
): string | null => {
  const clientId = import.meta.env.VITE_INTEGRATION_HUBSPOT_CLIENT_ID;
  if (!clientId) return null;

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    scope: HUBSPOT_OAUTH_SCOPES.join(" "),
    state,
  });

  return `https://app.hubspot.com/oauth/authorize?${params.toString()}`;
};

export const getIntegrations = (t: TFunction): IntegrationCategory[] => [
  {
    category: "shaarp",
    title: t("team.integrations.categories.shaarp"),
    items: [
      {
        key: "chromeExtension",
        title: t("team.integrations.items.chromeExtension.name"),
        description: t("team.integrations.items.chromeExtension.description"),
        available: true,
        beta: false,
        logo: chromeIcon,
        link: "/integrations/chrome-extension",
      },
      {
        key: "edgeExtension",
        title: t("team.integrations.items.edgeExtension.name"),
        description: t("team.integrations.items.edgeExtension.description"),
        available: true,
        beta: false,
        logo: edgeIcon,
        link: "/integrations/edge-extension",
      },
      {
        key: "apiKeys",
        title: t("team.integrations.items.apiKeys.name"),
        description: t("team.integrations.items.apiKeys.description"),
        available: true,
        link: "/integrations/api",
        beta: false,
        logo: apiKeysIcon,
      },
    ],
  },
  {
    category: "calendar",
    title: t("team.integrations.categories.calendar"),
    items: [
      {
        key: "googleCalendar",
        title: t("team.integrations.items.googleCalendar.name"),
        description: t("team.integrations.items.googleCalendar.description"),
        available: true,
        beta: false,
        link: "/integrations/google-calendar",
        logo: googleCalendarIcon,
      },
      {
        key: "outlookCalendar",
        title: t("team.integrations.items.outlookCalendar.name"),
        description: t("team.integrations.items.outlookCalendar.description"),
        available: true,
        beta: false,
        link: "/integrations/outlook-calendar",
        logo: outlookCalendarIcon,
      },
    ],
  },
  {
    category: "mail",
    title: t("team.integrations.categories.mail"),
    items: [
      {
        key: "gmail",
        title: t("team.integrations.items.gmail.name"),
        description: t("team.integrations.items.gmail.description"),
        available: false,
        beta: false,
        logo: gmailIcon,
      },
      {
        key: "outlook",
        title: t("team.integrations.items.outlook.name"),
        description: t("team.integrations.items.outlook.description"),
        available: false,
        beta: false,
        logo: outlookIcon,
      },
    ],
  },
  {
    category: "crm",
    title: t("team.integrations.categories.crm"),
    items: [
      {
        key: "hubspot",
        title: t("team.integrations.items.hubspot.name"),
        description: t("team.integrations.items.hubspot.description"),
        available: true,
        beta: false,
        link: "/integrations/hubspot",
        logo: hubspotIcon,
      },
      {
        key: "seliaris",
        title: t("team.integrations.items.seliaris.name"),
        description: t("team.integrations.items.seliaris.description"),
        available: true,
        beta: true,
        link: "/integrations/seliaris",
        logo: seliarisIcon,
      },
      {
        key: "salesforce",
        title: t("team.integrations.items.salesforce.name"),
        description: t("team.integrations.items.salesforce.description"),
        available: false,
        beta: false,
        logo: salesforceIcon,
      },
      {
        key: "pipedrive",
        title: t("team.integrations.items.pipedrive.name"),
        description: t("team.integrations.items.pipedrive.description"),
        available: false,
        beta: false,
        logo: pipedriveIcon,
      },
      {
        key: "dynamics365",
        title: t("team.integrations.items.dynamics365.name"),
        description: t("team.integrations.items.dynamics365.description"),
        available: false,
        beta: false,
        logo: dynamics365Icon,
      },
      {
        key: "odoo",
        title: t("team.integrations.items.odoo.name"),
        description: t("team.integrations.items.odoo.description"),
        available: false,
        beta: false,
        logo: odooIcon,
      },
    ],
  },
  {
    category: "workflow",
    title: t("team.integrations.categories.workflow"),
    items: [
      {
        key: "zapier",
        title: t("team.integrations.items.zapier.name"),
        description: t("team.integrations.items.zapier.description"),
        available: false,
        beta: false,
        logo: zapierIcon,
      },
      {
        key: "make",
        title: t("team.integrations.items.make.name"),
        description: t("team.integrations.items.make.description"),
        available: false,
        beta: false,
        logo: makeIcon,
      },
      {
        key: "n8n",
        title: t("team.integrations.items.n8n.name"),
        description: t("team.integrations.items.n8n.description"),
        available: false,
        beta: false,
        logo: n8nIcon,
      },
    ],
  },
];
